/**
 * The star: a point of light that flares into an X of diffraction spikes (the
 * X is read off the Manrope letterform) and then implodes: the spikes are drawn
 * back in and the star shrinks to a pinpoint. Everything is a closed-form
 * function of time, so the timeline scrubs freely.
 */
import { rangeProgress, smootherstep, smoothstep } from '../easing'
import { glow, spike } from '../draw'
import type { Canvas2D } from '../renderer/renderer'
import type { Viewport } from '../viewport'
import { T_DET } from './supernova'

/* =====================================================================
   TWEAKABLES. Times are seconds; lengths are in units of min(width, height).
   ===================================================================== */
const POINT_IN: [number, number] = [0.1, 0.5] /* the point fades in */
const FLARE: [number, number] = [
  0.5, 1.35,
] /* it brightens and its four spikes grow into the X of the word */
const X_SIZE = 1.9 /* flare size against the word's own X (1 = spikes end at the letter's corners) */
const TWINKLE = 0.07 /* how much the held star shimmers */
export const IMPLODE: readonly [number, number] = [
  3.3, 3.75,
] /* the X is drawn back in, the star shrinks to a pinpoint (accelerating) */
const SPIKE_W = 0.003 /* spike width at the core */
const HALO_R = 0.05 /* the glow round the star: radius, */
const HALO_A = 0.6 /* ...brightness */
const CORE_R = 0.012 /* the bright core round the point */
/* ===================================================================== */

const REFERENCE_SIZE = 300 /* the size the X is measured at (px) */
const SANS = "Manrope, 'Helvetica Neue', Helvetica, Arial, sans-serif"
/** Loading this face is what makes the letterform measurable; ask the page's fonts for it. */
export const STAR_FONT = '200 ' + REFERENCE_SIZE + 'px Manrope'

/** One of the star's four spikes: its direction on screen (rad) and how wide it is, relative to the X's height. */
export interface StarArm {
  readonly angle: number
  readonly spread: number
}

/** The four arms of an X drawn square on, for when the letterform cannot be measured. */
export const SQUARE_ARMS: readonly StarArm[] = [0, 1, 2, 3].map((i) => ({
  angle: Math.PI / 4 + (i * Math.PI) / 2,
  spread: 0.1,
}))

type Span = [number, number]

/**
 * Measures the X's four arms off the Manrope 200 letterform: where each of its
 * strokes leaves the corners. Null when the letter cannot be measured, say
 * because the font has not loaded.
 */
export function measureArms(): readonly StarArm[] | null {
  const canvas = document.createElement('canvas')
  let g = canvas.getContext('2d')!
  const font = '200 ' + REFERENCE_SIZE + 'px ' + SANS
  g.font = font
  const m = g.measureText('X')
  const ascent = m.actualBoundingBoxAscent || REFERENCE_SIZE * 0.72
  const descent = m.actualBoundingBoxDescent || 0
  const w = Math.ceil(m.width) + 10
  const h = Math.ceil(ascent + descent) + 10
  canvas.width = w
  canvas.height = h
  g = canvas.getContext('2d')!
  g.font = font
  g.textBaseline = 'alphabetic'
  g.fillStyle = '#fff'
  g.fillText('X', 5, 5 + ascent)
  const data = g.getImageData(0, 0, w, h).data
  function inked(x: number, y: number): boolean {
    return data[(y * w + x) * 4 + 3] > 120
  }

  /** The runs of ink along row `y`. */
  function spans(y: number): Span[] {
    const out: Span[] = []
    let open = false
    let from = 0
    for (let x = 0; x < w; x++) {
      const on = inked(x, y)
      if (on && !open) {
        open = true
        from = x
      } else if (!on && open) {
        open = false
        if (x - from > 1) out.push([from, x - 1])
      }
    }
    if (open && w - from > 1) out.push([from, w - 1])
    return out
  }

  let top = -1
  let bottom = -1
  for (let y = 0; y < h && top < 0; y++) {
    for (let x = 0; x < w; x++) {
      if (inked(x, y)) {
        top = y
        break
      }
    }
  }
  for (let y = h - 1; y >= 0 && bottom < 0; y--) {
    for (let x = 0; x < w; x++) {
      if (inked(x, y)) {
        bottom = y
        break
      }
    }
  }
  if (top < 0 || bottom - top < 10) return null
  const topRow = top + 2
  const bottomRow = bottom - 2
  const topSpans = spans(topRow).sort((a, b) => a[0] - b[0])
  const bottomSpans = spans(bottomRow).sort((a, b) => a[0] - b[0])
  if (topSpans.length < 2 || bottomSpans.length < 2) return null
  const half = (bottom - top) / 2
  const centreX = w / 2
  const centreY = (top + bottom) / 2
  return [
    { span: topSpans[0], y: topRow },
    { span: topSpans[topSpans.length - 1], y: topRow },
    { span: bottomSpans[0], y: bottomRow },
    { span: bottomSpans[bottomSpans.length - 1], y: bottomRow },
  ].map(({ span, y }) => {
    const mx = ((span[0] + span[1]) / 2 - centreX) / half
    const my = (y - centreY) / half
    return { angle: Math.atan2(my, mx), spread: (span[1] - span[0]) / 2 / half }
  })
}

/**
 * The star's core, halo and four diffraction spikes in an X. `intensity` is its
 * brightness, `length` the spikes' length in px, `time` the shimmer.
 */
function drawSpikes(
  ctx: Canvas2D,
  vp: Viewport,
  arms: readonly StarArm[],
  intensity: number,
  length: number,
  time: number
) {
  const I = intensity
  const w = Math.max(vp.min * SPIKE_W * (0.5 + 0.5 * Math.min(I, 2)), 0.6)
  for (let a = 0; a < 4; a++) {
    const angle = arms[a].angle
    const len = length * (1 + 0.04 * Math.sin(time * 3 + a * 1.7))
    /* a wide warm fringe, a faint cool one just inside, the white core line: the
       slight colour split real diffraction spikes have */
    spike(ctx, vp.cx, vp.cy, angle, len * 0.82, w * 3.2, '255,196,128', 0.12 * I)
    spike(ctx, vp.cx, vp.cy, angle, len * 0.92, w * 1.8, '214,226,255', 0.16 * I)
    spike(ctx, vp.cx, vp.cy, angle, len, w * 0.7, '255,255,255', Math.min(0.95, 0.6 * I))
  }
  glow(
    ctx,
    vp.cx,
    vp.cy,
    vp.min * HALO_R * Math.min(I, 2.2),
    HALO_A * Math.min(I, 1.4) * 0.6,
    0.18,
    '236,240,255'
  )
  glow(ctx, vp.cx, vp.cy, vp.min * CORE_R * (0.5 + Math.min(I, 2)), Math.min(1, 0.9 * I), 0.3)
  ctx.fillStyle = 'rgba(255,255,255,' + Math.min(1, 0.4 + I).toFixed(3) + ')'
  ctx.beginPath()
  ctx.arc(vp.cx, vp.cy, Math.max(vp.min * 0.0022 * (1 + 0.6 * Math.min(I, 2)), 0.8), 0, 6.2832)
  ctx.fill()
}

/**
 * The star at time `t` before the supernova: the point fading in, the flare
 * into the word's X, and the implosion down to a pinpoint. `armLength` is the
 * word's X, centre to corner, in px.
 */
export function drawStar(
  ctx: Canvas2D,
  vp: Viewport,
  arms: readonly StarArm[],
  armLength: number,
  t: number
) {
  const pin = smoothstep(POINT_IN[0], POINT_IN[1], t)
  const flare = smootherstep(FLARE[0], FLARE[1], t)
  const implodeProgress = rangeProgress(IMPLODE, t)
  const implode = Math.pow(implodeProgress, 2.2) /* accelerating */
  const shimmer = 1 + TWINKLE * (Math.sin(t * 11.0) * 0.6 + Math.sin(t * 23.0) * 0.4) * flare
  let intensity = (0.25 * pin + 1.15 * flare) * shimmer
  let length = armLength * X_SIZE * (0.12 * pin + 0.88 * flare) * (1 + 0.5 * (shimmer - 1))
  /* implosion: spikes retract faster than the light dies, down to a hard pinpoint */
  length *= 1 - implode
  intensity = intensity * (1 - implode) + 0.5 * implode
  if (intensity > 0.004 && length > 1) drawSpikes(ctx, vp, arms, intensity, length, t)
  else glow(ctx, vp.cx, vp.cy, vp.min * 0.012, 0.6 * implode, 0.3)
  const pinpoint = smoothstep(IMPLODE[0], IMPLODE[1], t) * (1 - smoothstep(T_DET - 0.02, T_DET, t))
  if (pinpoint > 0.01) {
    ctx.fillStyle = 'rgba(255,255,255,' + pinpoint.toFixed(3) + ')'
    ctx.beginPath()
    ctx.arc(vp.cx, vp.cy, Math.max(vp.min * 0.0016 * (1 - 0.5 * implodeProgress), 0.7), 0, 6.2832)
    ctx.fill()
  }
}
